import type { StatusMeterView } from '#contract'
import { useView } from '../app/use-view.ts'
import { useT } from '../i18n/index.ts'
import styles from './status-panel.module.css'

type Tier = 'critical' | 'low' | 'fair' | 'good'

/** A meter's colour band: under 20 red, under 40 orange, under 60 yellow, else green. */
function tierOf(meter: StatusMeterView): Tier {
  const good = meter.highIsBad ? 100 - meter.value : meter.value
  if (good < 20) return 'critical'
  if (good < 40) return 'low'
  if (good < 60) return 'fair'
  return 'good'
}

/**
 * The player's meters, bottom left, two to a row: each name, then a ring filled to its value
 * and coloured by how well it stands, so all-green reads as fine at a glance. Hidden with no
 * player.
 */
export function StatusPanel() {
  const meters = useView((view) => view.status)
  if (meters === null || meters.length === 0) return null

  return (
    <dl class={styles.panel}>
      {meters.map((meter) => (
        <Meter key={meter.id} meter={meter} />
      ))}
    </dl>
  )
}

function Meter({ meter }: { readonly meter: StatusMeterView }) {
  const t = useT()
  return (
    <div class={styles.meter}>
      <dt class={styles.name}>{t(meter.label)}</dt>
      {/* A `dd` can't take a role, so the meter is a span inside it. */}
      <dd class={styles.value} data-tier={tierOf(meter)}>
        <span
          class={styles.gauge}
          role="meter"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={meter.value}
        >
          {/* pathLength 100, so the dash is the value; no fill at 0, or the round cap shows a dot. */}
          <svg class={styles.ring} viewBox="0 0 20 20" aria-hidden="true">
            <circle class={styles.track} cx="10" cy="10" r="8" pathLength={100} />
            {meter.value > 0 && (
              <circle
                class={styles.fill}
                cx="10"
                cy="10"
                r="8"
                pathLength={100}
                stroke-dashoffset={100 - meter.value}
              />
            )}
          </svg>
        </span>
      </dd>
    </div>
  )
}
